// use client: activeTag filter + pagination state
'use client'

import { useEffect, useState } from 'react'
import { Col, Row, Stack } from 'react-bootstrap'

import Link from '@/components/Link'

import './report.css'

import type { CmsItem } from '@/utils/api'

import moment from 'moment'

import defaultThumbnail from '@/assets/images/sustainability-thumbnail.webp'

function SustainabilityReport({
  initialData
}: {
  initialData?: CmsItem<'/sustainability-reports'>[] | null
}) {
  const initialReports = initialData ?? []

  const extractedTags = [...new Set(initialReports.map(item => item.tag))]
    .filter((t): t is string => t !== null && t !== undefined)
    .sort((a, b) => Number(b) - Number(a))

  const initialTags = [...extractedTags, 'Archive']

  // Content comes from the build-time snapshot only. Every caller now seeds
  // `initialData`, so the old `if (!initialData)` fetch branch was dead. A Strapi
  // publish rebuilds and redeploys the site, so there is nothing to revalidate on
  // mount. See tests/no-client-cms-fetch.spec.ts.
  const tags = initialTags
  const sustainabilityReportList = initialReports
  const [activeTag, setActiveTag] = useState(initialTags[0] ?? '')

  // Pagination state
  const ITEMS_PER_PAGE = 9
  const [currentPage, setCurrentPage] = useState(1)

  // Reset page on filter change
  useEffect(() => {
    setCurrentPage(1)
  }, [activeTag])

  const latestYear = Math.max(...sustainabilityReportList.map(item => Number(item.tag)))

  // Filtered data
  const filteredData = sustainabilityReportList.filter(x => {
    if (activeTag === 'Archive') {
      return Number(x.tag) !== latestYear
    }
    return x.tag === activeTag
  })

  // Pagination logic
  const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE)

  const paginatedData = filteredData.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  )
  return (
    <div className="container page">
      <h1 className="page-title">Sustainability Reports</h1>

      {/* TAG BUTTONS */}
      <Stack direction="horizontal" gap={3}>
        {tags.map(item => (
          <button
            key={item}
            type="button"
            className={activeTag === item ? 'btn-tag active' : 'btn-tag'}
            aria-pressed={activeTag === item}
            onClick={() => setActiveTag(item)}
          >
            {item}
          </button>
        ))}
      </Stack>

      {/* CARDS */}
      <div className="report-section-new">
        <Row className="g-4">
          {paginatedData.map(item => (
            <Col key={item.id} md={6} lg={4}>
              <Link
                href={item.pdfLink || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="report-card-link"
              >
                <div className="report-card-new">
                  <div className="report-image">
                    <img
                      src={item.image?.url || defaultThumbnail.src}
                      alt={item.title || 'Report image'}
                    />
                  </div>

                  <div className="report-content">
                    <h3>
                      {(item.title ?? '').length < 100
                        ? item.title
                        : (item.title ?? '').slice(0, 100) + '...'}
                    </h3>
                    {/*
                    {item.description && (
                      <p>
                        {item.description.length < 130
                          ? parse(item.description)
                          : parse(item.description.slice(0, 130) + '...')}
                      </p>
                    )}
                    */}

                    <div className="report-footer">
                      <span className="view-report">View Report</span>
                      <p>{moment(item.date).format('DD MMM YYYY')}</p>
                    </div>
                  </div>
                </div>
              </Link>
            </Col>
          ))}
        </Row>

        {/* PAGINATION */}
        {totalPages > 1 && (
          <div className="pagination-wrapper">
            {/* Prev */}
            <button
              className="pagination-btn"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(prev => prev - 1)}
            >
              {'<'}
            </button>

            {/* Numbers */}
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                className={currentPage === page ? 'pagination-number active' : 'pagination-number'}
                onClick={() => setCurrentPage(page)}
              >
                {page}
              </button>
            ))}

            {/* Next */}
            <button
              className="pagination-btn"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(prev => prev + 1)}
            >
              {'>'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default SustainabilityReport
